// Menus from the steering wheel, so a wheel player never needs the mouse:
// the paddles (or a flick of the wheel) move between the buttons of whichever
// overlay is on top, the rim's select button presses the highlighted one, and
// the pause button backs out. Works on every overlay generically by walking
// its visible buttons in document order. Game controllers use it too (D-pad
// and bumpers move, A/✕ selects, B/○ backs out).

const BACK_LABEL = /^(back|done|resume|ok|close)$/i;

export class WheelMenuNav {
  private focused: HTMLElement | undefined;
  private overlay: HTMLElement | undefined;
  /** Steering flick state: −1/1 while the wheel is held past the threshold. */
  private flick = 0;

  constructor(private readonly root: HTMLElement) {}

  /** The overlay on top (highest z-index, later in the page on a tie), if any is open. */
  activeOverlay(): HTMLElement | undefined {
    let best: HTMLElement | undefined;
    let bestZ = -Infinity;
    for (const el of this.root.querySelectorAll<HTMLElement>('.overlay.show')) {
      const z = Number(getComputedStyle(el).zIndex) || 0;
      if (z >= bestZ) {
        best = el;
        bestZ = z;
      }
    }
    return best;
  }

  private buttons(overlay: HTMLElement): HTMLElement[] {
    return [...overlay.querySelectorAll<HTMLElement>('button, [role="button"]')].filter(
      (b) => !(b as HTMLButtonElement).disabled && b.getClientRects().length > 0 && getComputedStyle(b).visibility !== 'hidden',
    );
  }

  /**
   * Per frame while a wheel is connected: follow the top overlay, highlighting
   * its first button when a new one opens so "select" works straight away.
   */
  update(): void {
    const overlay = this.activeOverlay();
    if (overlay !== this.overlay) {
      this.overlay = overlay;
      this.setFocus(overlay ? this.buttons(overlay)[0] : undefined);
    } else if (this.focused && !this.focused.isConnected) {
      this.setFocus(overlay ? this.buttons(overlay)[0] : undefined);
    }
  }

  /** Forget the highlight (no wheel, or the game is being driven). */
  reset(): void {
    this.overlay = undefined;
    this.setFocus(undefined);
  }

  move(dir: 1 | -1): void {
    const overlay = this.activeOverlay();
    if (!overlay) return;
    const list = this.buttons(overlay);
    if (!list.length) return;
    const i = this.focused ? list.indexOf(this.focused) : -1;
    const next = i < 0 ? (dir > 0 ? 0 : list.length - 1) : (i + dir + list.length) % list.length;
    this.setFocus(list[next]);
  }

  /** Press the highlighted button (or highlight the first one). Returns true if something was clicked. */
  select(): boolean {
    const overlay = this.activeOverlay();
    if (!overlay) return false;
    if (this.focused && overlay.contains(this.focused) && this.buttons(overlay).includes(this.focused)) {
      this.focused.click();
      return true;
    }
    this.move(1);
    return false;
  }

  /** Press the overlay's Back / Done / OK style button, if it has one. */
  back(): boolean {
    const overlay = this.activeOverlay();
    if (!overlay) return false;
    const list = this.buttons(overlay);
    const target =
      list.find((b) => BACK_LABEL.test(b.textContent?.trim() ?? '')) ?? list.find((b) => b.classList.contains('secondary') || b.classList.contains('message-btn'));
    target?.click();
    return !!target;
  }

  /** Steering as a menu control: flick past a third of lock to step left/right. */
  steer(value: number): void {
    const dir = value > 0.35 ? 1 : value < -0.35 ? -1 : 0;
    if (dir !== 0 && dir !== this.flick) this.move(dir);
    if (dir !== 0) this.flick = dir;
    else if (Math.abs(value) < 0.2) this.flick = 0;
  }

  private setFocus(el: HTMLElement | undefined): void {
    this.focused?.classList.remove('wheel-focus');
    this.focused = el;
    if (!el) return;
    el.classList.add('wheel-focus');
    el.focus({ preventScroll: true });
    el.scrollIntoView({ block: 'nearest' });
  }
}
